import { Box, BoxProps } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'

import { useTranslations } from '~shared-components/hooks/useTranslations'

import { formatDate } from '../../utils/misc'
import { InputDatePicker } from '..'
import { HorizontalDivider } from '../Card/CardDivider'
import { StandardText } from '../Typography/Typography'
import {
  DefaultButtonDisplay,
  DefaultOptionDisplay,
  DropdownButtonDisplayProps,
  DropdownOption,
  DropdownOptionDisplayProps,
  StandardDropdown,
} from './StandardDropdown'

export interface DateSelectionDropdownOption extends DropdownOption {
  end: Date
  start: Date
}
export type DateSelectionChange = {
  endDate?: Date
  quickRange?: DateSelectionDropdownOption
  startDate?: Date
}

export type CurrentDateSelection = { endDate?: string; startDate?: string }

export type DateSelectionOnChangeFn = (
  selectionChange: DateSelectionChange,
) => void

const sxDatePickerContainer = {
  '& > div': {
    margin: '8px 0',
  },
}

const DatePickerContainer: React.FC<BoxProps> = props => (
  <Box sx={sxDatePickerContainer} {...props} />
)

const findFromQuickRange = (
  quickRange?: DateSelectionDropdownOption[],
  start?: Date,
  end?: Date,
) => {
  const dateToDaysOnly = (date?: Date) =>
    date && new Date(date.getFullYear(), date.getMonth(), date.getDate())

  const newStart = dateToDaysOnly(start)
  const newEnd = dateToDaysOnly(end)
  const newRange = quickRange?.map(r => ({
    ...r,
    end: dateToDaysOnly(r.end),
    start: dateToDaysOnly(r.start),
  }))
  return newRange?.find(
    r =>
      r.start?.getTime() === newStart?.getTime() &&
      r.end?.getTime() === newEnd?.getTime(),
  )
}

export interface DateSelectionDropdownProps {
  currentSelection?: CurrentDateSelection
  'data-testid'?: string
  dateFormatter?: (date: Date) => string
  onDateSelectionChange?: DateSelectionOnChangeFn
  quickRangeOptions?: DateSelectionDropdownOption[]
  staticDisplayValue?: string
}

export const DateSelectionDropdown = ({
  quickRangeOptions = [],
  'data-testid': dataTestId,
  staticDisplayValue,
  dateFormatter = (date: Date) => formatDate(date, '/'),
  onDateSelectionChange,
  currentSelection,
}: DateSelectionDropdownProps) => {
  const [internalValue, setInternalValue] = useState<
    undefined | DateSelectionDropdownOption['value']
  >('')
  const [startDate, setStartDate] = useState<Date | undefined>()
  const [endDate, setEndDate] = useState<Date | undefined>()
  const [quickRangeOption, setQuickRangeOption] = useState<
    DateSelectionDropdownOption | undefined
  >()
  const t = useTranslations()

  const setRangeDisplayValue = (
    start: Date | undefined,
    end: Date | undefined,
  ) => {
    let newValue = ''
    if (start || end) {
      const startText = start ? dateFormatter(start) : '...'
      const endtext = end ? dateFormatter(end) : '...'
      newValue = `${startText} - ${endtext}`
    }

    setInternalValue(newValue)
  }

  const onChange = (
    start: Date | undefined,
    end: Date | undefined,
    selectedOption: DateSelectionDropdownOption | undefined,
  ) => {
    setQuickRangeOption(selectedOption)
    setStartDate(start)
    setEndDate(end)

    if (onDateSelectionChange && [start, end, quickRangeOption].some(Boolean)) {
      onDateSelectionChange({
        endDate: end,
        quickRange: quickRangeOption,
        startDate: start,
      })
    }
  }

  const onStartDateChange = (start: Date) => {
    const resetEndDate = endDate && start > endDate
    start.setHours(0, 0, 0)
    onChange(start, resetEndDate ? undefined : endDate, undefined)
  }

  const onEndDateChange = (end: Date) => {
    const resetStartDate = startDate && end < startDate
    end.setHours(23, 59, 59)
    onChange(resetStartDate ? undefined : startDate, end, undefined)
  }

  const onQuickRange = (value: DateSelectionDropdownOption['value']) => {
    const selectedOption = quickRangeOptions?.find(o => o.value === value)
    if (selectedOption) {
      onChange(selectedOption.start, selectedOption.end, selectedOption)
    }
  }

  useEffect(() => {
    // Allow outside filter changes to clear the dates in the component
    if (currentSelection === undefined) return

    const {
      endDate: currentEndDate,
      startDate: currentStartDate,
    } = currentSelection
    const start = currentStartDate ? new Date(currentStartDate) : undefined
    const end = currentEndDate ? new Date(currentEndDate) : undefined

    const selectedOption = findFromQuickRange(quickRangeOptions, start, end)
    if (selectedOption !== undefined) {
      setInternalValue(selectedOption.description)
    } else {
      setRangeDisplayValue(start, end)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelection, quickRangeOptions])

  type QuickRangeOptionDisplayProps = DropdownOptionDisplayProps<DateSelectionDropdownOption>

  const QuickRangeOptionDisplay: React.FC<QuickRangeOptionDisplayProps> = ({
    option,
  }) => {
    const isSelected =
      option.start?.valueOf() === startDate?.valueOf() &&
      option.end?.valueOf() === endDate?.valueOf()
    return <DefaultOptionDisplay isSelected={isSelected} option={option} />
  }

  type DateSelectionButtonDisplayProps = DropdownButtonDisplayProps<DateSelectionDropdownOption>

  const DateSelectionButtonDisplay: React.FC<DateSelectionButtonDisplayProps> = ({
    options,
    ...props
  }) => {
    const displayValue = internalValue
      ? findFromQuickRange(quickRangeOptions, startDate, endDate)
          ?.description || internalValue
      : staticDisplayValue
    return (
      <DefaultButtonDisplay
        {...props}
        options={options as DropdownOption[]}
        value={displayValue}
      />
    )
  }

  return (
    <StandardDropdown
      aria-labelledby="category"
      data-testid={dataTestId}
      displayButton={DateSelectionButtonDisplay}
      displayOption={QuickRangeOptionDisplay}
      maxHeight="auto"
      onOptionSelect={onQuickRange}
      options={quickRangeOptions}
      overflowY="visible"
      value={internalValue}
    >
      <Box px={5}>
        <HorizontalDivider my={5} />
        <DatePickerContainer>
          <StandardText>{t('filter.startdate')}</StandardText>
          <InputDatePicker
            onDateChange={date => onStartDateChange(date)}
            placeholder="DD.MM.YYYY"
            value={currentSelection?.startDate || ''}
          />
        </DatePickerContainer>
        <DatePickerContainer>
          <StandardText>{t('filter.enddate')}</StandardText>
          <InputDatePicker
            onDateChange={date => onEndDateChange(date)}
            placeholder="DD.MM.YYYY"
            value={currentSelection?.endDate || ''}
          />
        </DatePickerContainer>
      </Box>
    </StandardDropdown>
  )
}
